<template>
  <div class="page-outer">
    <!-- 固定頂部 Header -->
    <div class="page-header page-header-broadcast-create">
      <div class="header-left">
        <h1 class="page-title">{{ isRecurringEdit ? '編輯週期推播' : (isEditMode ? '編輯推播活動' : '新增推播活動') }}</h1>
      </div>
      <div class="header-actions">
        <button v-if="currentStep > 0" type="button" class="btn btn-secondary" @click="prevStep" :disabled="loading">
          <i class="ri-arrow-left-line"></i> 上一步
        </button>
        <button v-else type="button" class="btn btn-secondary" @click="handleCancel" :disabled="loading">
          取消
        </button>

        <button
          v-if="canSaveDraft"
          type="button"
          class="btn btn-outline-primary"
          @click="handleSaveDraft"
          :disabled="loading || savingDraft || !canSaveDraft"
        >
          <span v-if="savingDraft">儲存中...</span>
          <span v-else><i class="ri-save-line"></i> 儲存草稿</span>
        </button>

        <button
          v-if="currentStep < steps.length - 1"
          type="button"
          class="btn btn-primary"
          @click="nextStep"
          :disabled="loading"
        >
          下一步 <i class="ri-arrow-right-line"></i>
        </button>

        <button
          v-else
          type="button"
          class="btn btn-primary"
          @click="handleSubmit"
          :disabled="loading || !isFormValid"
        >
          <span v-if="loading">{{ form.sendType === 'scheduled' ? '設定中...' : '發送中...' }}</span>
          <span v-else>
            <i :class="form.sendType === 'scheduled' ? 'ri-calendar-schedule-line' : 'ri-send-plane-line'"></i>
            {{ submitButtonLabel }}
          </span>
        </button>
      </div>
    </div>

  <div class="page-container">

    <!-- 固定浮動預覽面板（四個步驟共用） -->
    <div class="global-preview-sticky">
      <div class="preview-title-row">
        <i class="ri-smartphone-line"></i>
        <span>訊息預覽</span>
        <span v-if="step1PreviewMessages.length > 1" class="preview-count-badge">共 {{ step1PreviewMessages.length }} 則</span>
      </div>
      <LineMessagePreview :messages="step1PreviewMessages" empty-text="設定訊息後即時預覽" />
    </div>

    <!-- 步驟指示器 -->
    <div class="steps-bar">
      <div
        v-for="(step, idx) in steps"
        :key="step.key"
        class="step-item"
        :class="{
          active: currentStep === idx,
          done: idx !== currentStep && (idx < currentStep || stepContentFilled[idx]),
          clickable: idx !== currentStep && isStepClickable(idx)
        }"
        @click="isStepClickable(idx) && idx !== currentStep ? goStep(idx) : null"
      >
        <div class="step-circle">
          <i v-if="idx !== currentStep && (idx < currentStep || stepContentFilled[idx])" class="ri-check-line"></i>
          <span v-else>{{ idx + 1 }}</span>
        </div>
        <div class="step-label">{{ step.label }}</div>
        <div v-if="idx < steps.length - 1" class="step-line" :class="{ done: currentStep > idx || stepContentFilled[idx] }"></div>
      </div>
    </div>

    <!-- 手機版全域預覽開關 -->
    <div class="mobile-preview-toggle-bar">
      <button class="mobile-preview-toggle-btn" @click="mobilePreviewOpen = !mobilePreviewOpen">
        <i :class="mobilePreviewOpen ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
        {{ mobilePreviewOpen ? '關閉預覽' : '開啟訊息預覽' }}
      </button>
    </div>
    <div v-if="mobilePreviewOpen" class="mobile-preview-drawer">
      <div class="mobile-preview-header">
        <span><i class="ri-smartphone-line"></i> 訊息預覽
          <span v-if="step1PreviewMessages.length > 1" class="preview-count-badge">共 {{ step1PreviewMessages.length }} 則</span>
        </span>
        <button class="mobile-preview-close" @click="mobilePreviewOpen = false"><i class="ri-close-line"></i></button>
      </div>
      <div class="mobile-preview-body">
        <LineMessagePreview :messages="step1PreviewMessages" empty-text="設定訊息後即時預覽" />
      </div>
    </div>

    <!-- 步驟內容 -->
    <div class="step-content">

      <!-- Step 0: 活動名稱 + 推播目標 -->
      <div v-show="currentStep === 0" class="form-section">
        <div class="section-header-row">
          <i class="ri-megaphone-line section-icon"></i>
          <h3>活動名稱與推播目標</h3>
        </div>

        <div class="form-group">
          <label class="form-label required-label">活動名稱</label>
          <input
            type="text"
            class="form-control"
            :class="{ 'is-invalid': stepErrors.campaignName }"
            v-model="form.campaignName"
            placeholder="例如：週年慶優惠活動"
            :disabled="loading"
          />
          <span v-if="stepErrors.campaignName" class="error-text">{{ stepErrors.campaignName }}</span>
          <p class="form-hint">
            <i class="ri-information-line"></i>
            僅供本系統後台辨識與搜尋，<strong>不會</strong>顯示在使用者的 LINE 通知預覽。通知上看到的是官方帳號名稱與「訊息內容」步驟設定的文字。
          </p>
        </div>

        <div class="form-group" style="margin-top: 24px;">
          <label class="form-label required-label">LINE 官方帳號</label>
          <div v-if="loadingConfigs" class="loading-text">載入中...</div>
          <div v-else-if="lineConfigs.length === 0" class="hint-box hint-warning">
            <i class="ri-alert-line"></i> 沒有可用的 LINE 官方帳號設定，請先建立 LINE 設定
          </div>
          <div v-else class="line-config-cards">
            <label
              v-for="config in lineConfigs"
              :key="config.id"
              class="config-card"
              :class="{ selected: form.lineConfigId === config.id, disabled: !config.isActive }"
            >
              <input
                type="radio"
                :value="config.id"
                v-model="form.lineConfigId"
                :disabled="loading || !config.isActive"
                @change="handleAudienceChange"
              />
              <div class="config-card-body">
                <i class="ri-line-fill config-icon"></i>
                <div>
                  <div class="config-name">{{ config.configName }}</div>
                  <span v-if="!config.isActive" class="badge badge-warning">未啟用</span>
                  <div class="config-followers" v-if="config.isActive">
                    <i class="ri-user-follow-line"></i>
                    <span v-if="config.followersCount !== null && config.followersCount !== undefined">
                      {{ config.followersCount.toLocaleString() }} 位追蹤者
                    </span>
                    <span v-else class="followers-unknown">追蹤者數未知</span>
                  </div>
                </div>
              </div>
            </label>
          </div>
          <span v-if="stepErrors.lineConfigId" class="error-text">{{ stepErrors.lineConfigId }}</span>
          <p class="form-hint"><i class="ri-information-line"></i> 請選擇一個 LINE 官方帳號作為推播目標</p>
        </div>
      </div>

      <!-- Step 1: 訊息內容 -->
      <div v-show="currentStep === 1" class="form-section">
        <div class="step1-messages">
        <div class="section-header-row">
          <i class="ri-chat-3-line section-icon"></i>
          <h3>訊息內容</h3>
          <span class="msg-count-badge">{{ form.messages.length }} / 5 則</span>
        </div>

        <p class="section-desc">最多可新增 5 則不同類型的訊息，LINE 會依序發送給收件人。</p>

        <!-- 訊息列表 -->
        <div class="messages-list">
          <div
            v-for="(msg, idx) in form.messages"
            :key="msg._id"
            class="message-block"
            :class="{ 'has-error': stepErrors[`msg_${idx}`] }"
          >
            <!-- 訊息標頭 -->
            <div class="msg-block-header" @click.self="msg._collapsed = !msg._collapsed" style="cursor:pointer;">
              <button type="button" class="icon-btn collapse-btn" @click="msg._collapsed = !msg._collapsed" :title="msg._collapsed ? '展開' : '收合'">
                <i :class="msg._collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'"></i>
              </button>
              <span class="msg-index">訊息 {{ idx + 1 }}</span>

              <!-- 收合時顯示摘要 -->
              <span v-if="msg._collapsed" class="msg-collapsed-summary">
                <i :class="{ 'ri-text': msg._inputMode === 'text', 'ri-image-line': msg._inputMode === 'image', 'ri-message-3-line': msg._inputMode === 'template' }"></i>
                <template v-if="msg._inputMode === 'text'">
                  {{ (msg._text || '').slice(0, 30) || '（未輸入文字）' }}
                </template>
                <template v-else-if="msg._inputMode === 'image'">
                  {{ msg._imageUrl ? '圖片已上傳' : '（未上傳圖片）' }}
                </template>
                <template v-else>
                  {{ msg._template ? msg._template.name : '（未選擇範本）' }}
                </template>
              </span>

              <!-- 上移 / 下移 / 刪除 -->
              <div class="msg-block-actions">
                <button type="button" class="icon-btn" :disabled="idx === 0 || loading" @click.stop="moveMsg(idx, -1)" title="上移">
                  <i class="ri-arrow-up-s-line"></i>
                </button>
                <button type="button" class="icon-btn" :disabled="idx === form.messages.length - 1 || loading" @click.stop="moveMsg(idx, 1)" title="下移">
                  <i class="ri-arrow-down-s-line"></i>
                </button>
                <button type="button" class="icon-btn danger" :disabled="form.messages.length <= 1 || loading" @click.stop="removeMsg(idx)" title="刪除">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>

            <!-- 訊息內容（收合時隱藏）-->
            <div v-if="!msg._collapsed" class="msg-block-body">
              <!-- 模式切換 -->
              <div class="input-mode-tabs">
                <button
                  type="button"
                  class="mode-tab"
                  :class="{ active: msg._inputMode === 'text' }"
                  @click="msg._inputMode = 'text'"
                >
                  <i class="ri-text"></i> 輸入文字
                </button>
                <button
                  type="button"
                  class="mode-tab"
                  :class="{ active: msg._inputMode === 'image' }"
                  @click="msg._inputMode = 'image'"
                >
                  <i class="ri-image-line"></i> 上傳圖片
                </button>
                <button
                  type="button"
                  class="mode-tab"
                  :class="{ active: msg._inputMode === 'template' }"
                  @click="msg._inputMode = 'template'"
                >
                  <i class="ri-message-3-line"></i> 選擇範本
                </button>
              </div>

              <!-- 範本選擇器 -->
              <LineMsgTemplatePicker
                v-if="msg._inputMode === 'template'"
                :ref="el => templatePickerRefs[idx] = el"
                audience-filter="broadcast"
                :model-value="msg._template"
                @update:model-value="val => { msg._template = val; activePreviewMsgIdx = idx; }"
              />

              <!-- 直接輸入文字 -->
              <div v-else-if="msg._inputMode === 'text'" class="text-input-wrap">
                <textarea
                  v-model="msg._text"
                  class="form-control message-textarea"
                  :class="{ 'is-invalid': stepErrors[`msg_${idx}`] }"
                  rows="6"
                  placeholder="請輸入推播文字內容"
                  @input="activePreviewMsgIdx = idx"
                ></textarea>
                <div class="char-count" :class="{ danger: (msg._text || '').length > 5000 }">
                  {{ (msg._text || '').length }} / 5000 字元
                </div>
              </div>

              <!-- 圖片上傳 -->
              <div v-else-if="msg._inputMode === 'image'" class="image-upload-wrap">
                <ImageUploader
                  v-model="msg._imageUrl"
                  :uploadFn="uploadImage"
                  shape="card"
                  accept="image/jpeg,image/png"
                  hint="僅支援 JPEG / PNG（LINE 不接受 WebP）"
                  size-hint="建議寬度 1040px 以上，最大 4MB，超過請先壓縮"
                  :max-size-mb="4"
                  @update:model-value="activePreviewMsgIdx = idx"
                />
                <p class="form-hint" style="margin-top:8px;">
                  <i class="ri-information-line"></i> 圖片須為 HTTPS 網址，LINE 不支援私有網域
                </p>
              </div>

              <div v-if="stepErrors[`msg_${idx}`]" class="error-text" style="margin-top:8px;">{{ stepErrors[`msg_${idx}`] }}</div>
            </div>
          </div>
        </div>

        <!-- 新增訊息按鈕 -->
        <button
          v-if="form.messages.length < 5"
          type="button"
          class="btn-add-msg"
          :disabled="loading"
          @click="addMsg"
        >
          <i class="ri-add-circle-line"></i> 新增訊息（{{ form.messages.length }}/5）
        </button>
        <div v-else class="hint-box hint-warning" style="margin-top:12px;">
          <i class="ri-alert-line"></i> 已達最多 5 則訊息上限
        </div>
        </div><!-- end step1-messages -->
      </div>

      <!-- Step 2: 受眾設定 -->
      <div v-show="currentStep === 2" class="form-section">
        <div class="section-header-row">
          <i class="ri-team-line section-icon"></i>
          <h3>受眾設定</h3>
        </div>

        <div class="form-group">
          <label class="form-label required-label">受眾類型</label>
          <div class="audience-type-cards">
            <label class="type-card" :class="{ selected: form.audienceType === 'rule' }">
              <input type="radio" value="rule" v-model="form.audienceType" :disabled="loading" @change="handleAudienceTypeChange" />
              <i class="ri-filter-line type-icon"></i>
              <div>
                <span>受眾規則</span>
                <small>依條件自動篩選</small>
              </div>
            </label>
            <label class="type-card" :class="{ selected: form.audienceType === 'members' }">
              <input type="radio" value="members" v-model="form.audienceType" :disabled="loading" @change="handleAudienceTypeChange" />
              <i class="ri-user-search-line type-icon"></i>
              <div>
                <span>手動篩選</span>
                <small>自行搜尋或上傳名單</small>
              </div>
            </label>
            <label class="type-card" :class="{ selected: form.audienceType === 'all' }">
              <input type="radio" value="all" v-model="form.audienceType" :disabled="loading" @change="handleAudienceTypeChange" />
              <i class="ri-broadcast-line type-icon"></i>
              <div>
                <span>全部好友廣播</span>
                <small>發送給所有 LINE 好友</small>
              </div>
            </label>
          </div>
          <div v-if="form.audienceType === 'all'" class="all-broadcast-notice">
            <i class="ri-information-line"></i>
            使用 LINE 廣播 API，訊息將發送給此官方帳號的<strong>全部追蹤者</strong>，無法指定特定對象。
          </div>
          <span v-if="stepErrors.audience" class="error-text">{{ stepErrors.audience }}</span>
        </div>

        <!-- 受眾規則選擇 -->
        <div v-if="form.audienceType === 'rule'" class="form-group" style="margin-top: 20px;">
          <label class="form-label required-label">受眾規則</label>
          <select
            class="form-control"
            v-model="form.ruleId"
            :disabled="loading || loadingRules"
            @change="handleAudienceChange"
          >
            <option value="">請選擇受眾規則</option>
            <option v-for="rule in audienceRules" :key="rule.id" :value="rule.id">
              {{ rule.name }}
              <template v-if="rule.audienceCount != null"> ({{ rule.audienceCount.toLocaleString() }} 人)</template>
              <template v-if="!rule.isActive">（已停用）</template>
            </option>
          </select>
          <small v-if="selectedRule" class="form-text">{{ selectedRule.description || '無說明' }}</small>
        </div>

        <!-- 手動選擇會員 -->
        <div v-if="form.audienceType === 'members'" style="margin-top: 20px;">
          <div class="selection-tabs">
            <button type="button" :class="['tab-button', { active: memberTab === 'search' }]" @click="memberTab = 'search'" :disabled="loading">
              <i class="ri-search-line"></i> 搜尋會員
            </button>
            <button type="button" :class="['tab-button', { active: memberTab === 'tags' }]" @click="memberTab = 'tags'" :disabled="loading">
              <i class="ri-price-tag-3-line"></i> 標籤搜尋
            </button>
            <button type="button" :class="['tab-button', { active: memberTab === 'csv' }]" @click="memberTab = 'csv'" :disabled="loading">
              <i class="ri-file-list-3-line"></i> 批量輸入
            </button>
          </div>
          <MemberSearch v-show="memberTab === 'search'" ref="searchRef" v-model="selectedMembers" :disabled="loading" />
          <MemberTagSearch v-show="memberTab === 'tags'" ref="tagSearchRef" v-model="selectedMembers" :disabled="loading" :line-config-id="form.lineConfigId || null" />
          <MemberCsvUpload v-show="memberTab === 'csv'" ref="csvRef" v-model="selectedMembers" :disabled="loading" />

          <div v-if="selectedMembers.length > 0" class="selected-members-section">
            <div class="section-header">
              <h4>已選擇的對象（{{ selectedMembers.length }} 位）</h4>
              <button type="button" class="btn btn-sm btn-danger" @click="clearAllMembers" :disabled="loading">
                <i class="ri-delete-bin-line"></i> 清除全部
              </button>
            </div>
            <div class="selected-members-list">
              <div v-for="member in selectedMembers" :key="member.id" class="selected-member-item">
                <div class="member-info">
                  <span class="member-name">
                    {{ member.name || member.lineUserId || member.id }}
                    <span v-if="member.isMember === false" class="badge-line-friend">LINE 好友</span>
                  </span>
                  <span v-if="member.phone" class="member-phone">{{ member.phone }}</span>
                  <span v-else-if="member.lineUserId && member.isMember === false" class="member-phone" style="font-family: monospace; font-size: 11px; color: #aaa;">{{ member.lineUserId }}</span>
                </div>
                <button type="button" class="btn btn-sm btn-link text-danger" @click="removeMember(member.id)" :disabled="loading">
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 預覽人數 -->
        <div class="preview-section" v-if="canPreview">
          <div class="preview-card">
            <div class="preview-header">
              <i class="ri-eye-line"></i>
              <span>預覽人數</span>
              <button v-if="form.audienceType !== 'members'" class="btn btn-sm btn-secondary" @click="previewAudience" :disabled="previewLoading" title="重新計算">
                <i class="ri-refresh-line" :class="{ 'spinning': previewLoading }"></i>
              </button>
            </div>
            <div class="preview-content">
              <div v-if="form.audienceType === 'members'" class="preview-result">
                <div class="preview-count">
                  <span class="count-value">{{ previewCount !== null ? previewCount.toLocaleString() : selectedMembers.length.toLocaleString() }}</span>
                  <span class="count-label">人</span>
                </div>
                <p class="preview-hint">
                  <i class="ri-information-line"></i>
                  <span v-if="previewCount !== null && previewCount !== selectedMembers.length">已選擇 {{ selectedMembers.length }} 位，其中 {{ previewCount.toLocaleString() }} 位有綁定 LINE 好友</span>
                  <span v-else-if="previewCount !== null">已選擇 {{ selectedMembers.length }} 位，全部都有綁定 LINE 好友</span>
                  <span v-else>已選擇 {{ selectedMembers.length }} 位，正在計算 LINE 好友數量...</span>
                </p>
              </div>
              <div v-else>
                <div v-if="previewLoading" class="preview-loading"><div class="spinner-small"></div><span>計算中...</span></div>
                <div v-else-if="previewError" class="preview-error"><i class="ri-error-warning-line"></i><span>{{ previewError }}</span></div>
                <div v-else-if="previewCount !== null" class="preview-result">
                  <div class="preview-count">
                    <span class="count-value">{{ previewCount.toLocaleString() }}</span>
                    <span class="count-label">人</span>
                  </div>
                  <p v-if="form.audienceType === 'all'" class="preview-hint">
                    <i class="ri-broadcast-line"></i> 目前有 {{ previewCount.toLocaleString() }} 位有效追蹤者，LINE 廣播 API 將發送給全部追蹤者
                  </p>
                  <p v-else class="preview-hint"><i class="ri-information-line"></i> 預計將發送給 {{ previewCount.toLocaleString() }} 位符合條件的 LINE 好友</p>
                </div>
                <div v-else class="preview-empty"><i class="ri-information-line"></i><span>請選擇受眾類型和 LINE 官方帳號以預覽人數</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: 發送設定 -->
      <div v-show="currentStep === 3" class="form-section">
        <!-- 左欄：發送設定 + 摘要 -->
        <div class="step3-form">
          <div class="section-header-row">
            <i class="ri-send-plane-line section-icon"></i>
            <h3>發送設定</h3>
          </div>

          <div class="form-group">
            <label class="form-label">發送方式</label>
            <div class="send-type-cards">
              <label v-if="!isRecurringEdit" class="type-card" :class="{ selected: form.sendType === 'immediate' }">
                <input type="radio" value="immediate" v-model="form.sendType" :disabled="loading" />
                <i class="ri-send-plane-fill type-icon"></i>
                <div>
                  <span>立即發送</span>
                  <small>建立後立即開始發送</small>
                </div>
              </label>
              <label v-if="!isRecurringEdit" class="type-card" :class="{ selected: form.sendType === 'scheduled' }">
                <input type="radio" value="scheduled" v-model="form.sendType" :disabled="loading" />
                <i class="ri-calendar-schedule-line type-icon"></i>
                <div>
                  <span>排程發送</span>
                  <small>指定時間發送</small>
                </div>
              </label>
              <label class="type-card" :class="{ selected: form.sendType === 'recurring' }">
                <input type="radio" value="recurring" v-model="form.sendType" :disabled="loading || isRecurringEdit" />
                <i class="ri-repeat-line type-icon"></i>
                <div>
                  <span>週期發送</span>
                  <small>按週期自動重複發送</small>
                </div>
              </label>
            </div>
          </div>

          <!-- 排程發送設定 -->
          <div v-if="form.sendType === 'scheduled'" class="form-group" style="margin-top: 20px;">
            <label class="form-label required-label">排程時間</label>
            <input
              type="datetime-local"
              class="form-control"
              :class="{ 'is-invalid': stepErrors.scheduledAt }"
              v-model="form.scheduledAt"
              :disabled="loading"
              style="max-width: 320px;"
            />
            <span v-if="stepErrors.scheduledAt" class="error-text">{{ stepErrors.scheduledAt }}</span>
            <p class="form-hint"><i class="ri-information-line"></i> 選擇推播發送的時間，時間必須在未來</p>
          </div>

          <!-- 週期發送設定 -->
          <div v-if="form.sendType === 'recurring'" class="recurring-settings" style="margin-top: 20px;">
            <span v-if="stepErrors.recurring" class="error-text" style="display:block;margin-bottom:12px;">{{ stepErrors.recurring }}</span>

            <!-- 週期類型 -->
            <div class="form-group">
              <label class="form-label required-label">週期類型</label>
              <div class="recurrence-type-tabs">
                <button type="button" v-for="t in [
                  { val: 'daily',   label: '每天' },
                  { val: 'weekly',  label: '每週' },
                  { val: 'monthly', label: '每月' },
                  { val: 'yearly',  label: '每年' }
                ]" :key="t.val"
                  class="recurrence-tab"
                  :class="{ active: form.recurring.recurrenceType === t.val }"
                  :disabled="loading"
                  @click="form.recurring.recurrenceType = t.val"
                >{{ t.label }}</button>
              </div>
            </div>

            <!-- 發送時間（整點） -->
            <div class="form-group">
              <label class="form-label required-label">發送時間</label>
              <select class="form-control" v-model="form.recurring.sendHour" :disabled="loading" style="max-width:160px;">
                <option v-for="h in 24" :key="h-1" :value="h-1">{{ String(h-1).padStart(2,'0') }}:00</option>
              </select>
            </div>

            <!-- 每週：星期幾 -->
            <div v-if="form.recurring.recurrenceType === 'weekly'" class="form-group">
              <label class="form-label required-label">發送星期</label>
              <div class="weekday-selector">
                <button
                  v-for="(name, idx) in ['一','二','三','四','五','六','日']"
                  :key="idx+1"
                  type="button"
                  class="weekday-btn"
                  :class="{ active: form.recurring.weeklyDays.includes(idx+1) }"
                  :disabled="loading"
                  @click="(() => {
                    const d = idx+1;
                    const arr = form.recurring.weeklyDays;
                    const i = arr.indexOf(d);
                    i === -1 ? arr.push(d) : arr.splice(i, 1);
                  })()"
                >週{{ name }}</button>
              </div>
            </div>

            <!-- 每月：日期 -->
            <div v-if="form.recurring.recurrenceType === 'monthly'" class="form-group">
              <label class="form-label required-label">發送日期</label>
              <div class="monthly-day-grid">
                <button
                  v-for="d in 31" :key="d"
                  type="button"
                  class="day-btn"
                  :class="{ active: form.recurring.monthlyDays.includes(d) }"
                  :disabled="loading"
                  @click="toggleMonthlyDay(d)"
                >{{ d }}</button>
                <button
                  type="button"
                  class="day-btn last-day-btn"
                  :class="{ active: form.recurring.monthlyDays.includes('last') }"
                  :disabled="loading"
                  @click="toggleMonthlyDay('last')"
                >最後一天</button>
              </div>
            </div>

            <!-- 每年 -->
            <div v-if="form.recurring.recurrenceType === 'yearly'" class="form-group">
              <label class="form-label required-label">日期類型</label>
              <div class="yearly-type-tabs">
                <button type="button"
                  class="recurrence-tab"
                  :class="{ active: form.recurring.yearlyType === 'gregorian' }"
                  :disabled="loading"
                  @click="form.recurring.yearlyType = 'gregorian'"
                >公曆指定日期</button>
                <button type="button"
                  class="recurrence-tab"
                  :class="{ active: form.recurring.yearlyType === 'lunar_festival' }"
                  :disabled="loading"
                  @click="form.recurring.yearlyType = 'lunar_festival'"
                >農曆節慶</button>
              </div>

              <!-- 公曆多日期 -->
              <div v-if="form.recurring.yearlyType === 'gregorian'" style="margin-top:12px;">
                <div
                  v-for="(dateItem, idx) in form.recurring.yearlyDates"
                  :key="idx"
                  class="yearly-date-row"
                >
                  <select class="form-control" v-model="dateItem.month" :disabled="loading" style="width:90px;">
                    <option v-for="m in 12" :key="m" :value="m">{{ m }} 月</option>
                  </select>
                  <select class="form-control" v-model="dateItem.day" :disabled="loading" style="width:80px;">
                    <option v-for="d in 31" :key="d" :value="d">{{ d }} 日</option>
                  </select>
                  <button
                    v-if="form.recurring.yearlyDates.length > 1"
                    type="button"
                    class="icon-btn danger"
                    :disabled="loading"
                    @click="removeYearlyDate(idx)"
                    title="移除此日期"
                  ><i class="ri-close-line"></i></button>
                </div>
                <button
                  v-if="form.recurring.yearlyDates.length < 12"
                  type="button"
                  class="btn-add-msg"
                  :disabled="loading"
                  @click="addYearlyDate"
                  style="margin-top:8px;"
                ><i class="ri-add-line"></i> 新增日期</button>
              </div>

              <!-- 農曆節慶 -->
              <div v-if="form.recurring.yearlyType === 'lunar_festival'" style="margin-top:12px;">
                <div class="form-group">
                  <label class="form-label required-label">節慶</label>
                  <select class="form-control" v-model="form.recurring.festival" :disabled="loading || loadingFestivals" style="max-width:200px;">
                    <option v-if="loadingFestivals" value="">載入中...</option>
                    <option v-for="f in festivals" :key="f.code" :value="f.code">{{ f.name }}</option>
                  </select>
                </div>
                <div class="form-group" style="margin-top:12px;">
                  <label class="form-label">發送時機</label>
                  <div class="festival-offset-row">
                    <select class="form-control" v-model.number="form.recurring.offsetDays" :disabled="loading" style="max-width:180px;">
                      <option :value="0">節慶當天</option>
                      <option v-for="n in 30" :key="-n" :value="-n">節慶前 {{ n }} 天</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- 進階設定（可收合） -->
            <div class="recurring-advanced-toggle" @click="showRecurringAdvanced = !showRecurringAdvanced">
              <i :class="showRecurringAdvanced ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
              <span>進階設定（開始/結束日期、最多次數）</span>
            </div>
            <div v-if="showRecurringAdvanced" class="recurring-advanced">
              <div class="form-row-group">
                <div class="form-group">
                  <label class="form-label">開始日期</label>
                  <input type="date" class="form-control" v-model="form.recurring.startDate" :disabled="loading" style="max-width:180px;" />
                  <p class="form-hint">留空表示立即生效</p>
                </div>
                <div class="form-group">
                  <label class="form-label">結束日期</label>
                  <input type="date" class="form-control" v-model="form.recurring.endDate" :disabled="loading" style="max-width:180px;" />
                  <p class="form-hint">留空表示永久執行</p>
                </div>
                <div class="form-group">
                  <label class="form-label">最多發送次數</label>
                  <input type="number" class="form-control" v-model="form.recurring.maxExecutions" :disabled="loading" min="1" placeholder="不限" style="max-width:120px;" />
                </div>
              </div>
            </div>
          </div>

          <!-- 確認摘要 -->
          <div class="summary-card" style="margin-top: 28px;">
            <h4 class="summary-title"><i class="ri-file-list-3-line"></i> 推播摘要</h4>
            <div class="summary-grid">
              <div class="summary-row">
                <span class="summary-key">活動名稱</span>
                <span class="summary-val">{{ form.campaignName || '—' }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-key">推播目標</span>
                <span class="summary-val">{{ selectedConfigName || '—' }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-key">訊息數量</span>
                <span class="summary-val">{{ form.messages.length }} 則（{{ messageSummaryLabel }}）</span>
              </div>
              <div class="summary-row">
                <span class="summary-key">受眾</span>
                <span class="summary-val">
                  <template v-if="form.audienceType === 'rule'">受眾規則：{{ selectedRule ? selectedRule.name : '—' }}</template>
                  <template v-else-if="form.audienceType === 'members'">手動選擇：{{ selectedMembers.length }} 位會員</template>
                  <template v-else-if="form.audienceType === 'all'">全部好友廣播（LINE Broadcast API）</template>
                  <template v-else>—</template>
                </span>
              </div>
              <div class="summary-row" v-if="form.audienceType !== 'all' && previewCount !== null">
                <span class="summary-key">預估發送</span>
                <span class="summary-val highlight">{{ previewCount.toLocaleString() }} 人</span>
              </div>
              <div class="summary-row">
                <span class="summary-key">發送方式</span>
                <span class="summary-val">
                  <template v-if="form.sendType === 'recurring'">週期：{{ recurringDescription }}</template>
                  <template v-else-if="form.sendType === 'scheduled'">排程：{{ form.scheduledAt ? formatScheduledAt(form.scheduledAt) : '—' }}</template>
                  <template v-else>立即發送</template>
                </span>
              </div>
            </div>
          </div>
        </div><!-- end step3-form -->
      </div>
    </div>

    <!-- 步驟導航按鈕 -->
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js';
import lineBroadcastService, { lineBroadcastRecurringService } from '@/api/modules/lineBroadcast.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import { audienceRuleService } from '@/api/modules/audienceRules.js';
import MemberSearch from '@/components/MemberSearch.vue';
import MemberTagSearch from '@/components/MemberTagSearch.vue';
import MemberCsvUpload from '@/components/MemberCsvUpload.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import LineMessagePreview from '@/components/LineMessagePreview.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';

export default {
  name: 'LineBroadcastCreate',
  components: { MemberSearch, MemberTagSearch, MemberCsvUpload, LineMsgTemplatePicker, LineMessagePreview, ImageUploader },
  setup() {
    const { pageTitle } = usePageTitle('新增推播活動');
    return { pageTitle };
  },
  data() {
    return {
      currentStep: 0,
      maxReachedStep: 0,
      mobilePreviewOpen: false,
      steps: [
        { key: 'basic',    label: '活動名稱・推播目標' },
        { key: 'message',  label: '訊息內容' },
        { key: 'audience', label: '受眾設定' },
        { key: 'send',     label: '發送設定' }
      ],
      stepErrors: {},
      loading: false,
      loadingConfigs: false,
      loadingRules: false,
      lineConfigs: [],
      audienceRules: [],
      previewLoading: false,
      previewCount: null,
      previewError: null,
      previewTimeout: null,
      savingDraft: false,
      memberTab: 'search',
      selectedMembers: [],
      templatePickerRefs: [],
      activePreviewMsgIdx: 0,
      form: {
        campaignName: '',
        lineConfigId: '',
        messages: [{ _id: 1, _inputMode: 'text', _template: null, _text: '', _imageUrl: '', _collapsed: false }],
        audienceType: 'rule',
        ruleId: '',
        sendType: 'immediate',
        scheduledAt: '',
        recurring: {
          recurrenceType: 'daily',
          sendHour: 9,
          sendMinute: 0,
          weeklyDays: [],
          monthlyDays: [],
          yearlyType: 'gregorian',
          yearlyDates: [{ month: 1, day: 1 }],
          festival: 'spring_new_year',
          offsetDays: 0,
          startDate: '',
          endDate: '',
          maxExecutions: ''
        }
      },
      festivals: [],
      loadingFestivals: false,
      showRecurringAdvanced: false
    };
  },
  computed: {
    isEditMode() {
      return !!this.$route.params.id;
    },
    isRecurringEdit() {
      return !!this.$route.meta?.recurringEdit;
    },
    isFormValid() {
      if (!this.form.campaignName || !this.form.lineConfigId) return false;
      if (this.form.messages.length === 0) return false;
      const anyMsgIncomplete = this.form.messages.some(msg => {
        if (msg._inputMode === 'text') return !(msg._text || '').trim();
        if (msg._inputMode === 'image') return !msg._imageUrl;
        return !msg._template;
      });
      if (anyMsgIncomplete) return false;
      if (!this.form.audienceType) return false;
      if (this.form.audienceType === 'rule' && !this.form.ruleId) return false;
      if (this.form.audienceType === 'members' && this.selectedMembers.length === 0) return false;
      if (this.form.sendType === 'scheduled' && !this.form.scheduledAt) return false;
      if (this.form.sendType === 'recurring') {
        const r = this.form.recurring;
        if (r.recurrenceType === 'weekly' && (!r.weeklyDays || r.weeklyDays.length === 0)) return false;
        if (r.recurrenceType === 'monthly' && (!r.monthlyDays || r.monthlyDays.length === 0)) return false;
        if (r.recurrenceType === 'yearly' && r.yearlyType === 'gregorian' && (!r.yearlyDates || r.yearlyDates.length === 0)) return false;
      }
      return true;
    },
    canPreview() {
      if (!this.form.lineConfigId) return false;
      if (this.form.audienceType === 'rule') return !!this.form.ruleId;
      if (this.form.audienceType === 'members') return this.selectedMembers.length > 0;
      if (this.form.audienceType === 'all') return true;
      return false;
    },
    selectedRule() {
      if (!this.form.ruleId) return null;
      return this.audienceRules.find(r => r.id === this.form.ruleId) || null;
    },
    canSaveDraft() {
      return !!this.form.campaignName;
    },
    selectedConfigName() {
      if (!this.form.lineConfigId) return null;
      const c = this.lineConfigs.find(c => c.id === this.form.lineConfigId);
      return c ? c.configName : null;
    },
    messageSummaryLabel() {
      const labels = this.form.messages.map(m => {
        if (m._inputMode === 'text') return '文字';
        if (m._inputMode === 'image') return '圖片';
        if (m._template) return m._template.name;
        return null;
      }).filter(Boolean);
      return labels.length > 0 ? labels.join('、') : '—';
    },

    step1PreviewMessages() {
      return this.form.messages
        .map(msg => {
          if (msg._inputMode === 'text') {
            return (msg._text || '').trim() ? { messageType: 'text', textContent: msg._text } : null;
          }
          if (msg._inputMode === 'image') {
            return msg._imageUrl ? { messageType: 'image', imageUrl: msg._imageUrl } : null;
          }
          return msg._template ? this._toPreviewData(msg._template) : null;
        })
        .filter(Boolean);
    },
    submitButtonLabel() {
      if (this.form.sendType === 'recurring') {
        return this.isEditMode ? '儲存週期設定' : '建立週期推播';
      }
      const isScheduled = this.form.sendType === 'scheduled';
      if (this.isEditMode) {
        return isScheduled ? '儲存並設定排程' : '儲存並立即發送';
      }
      return isScheduled ? '設定排程發送' : '立即發送';
    },

    recurringDescription() {
      const r = this.form.recurring;
      if (!r) return '';
      if (r.recurrenceType === 'daily') return `每天 ${String(r.sendHour).padStart(2, '0')}:00`;
      if (r.recurrenceType === 'weekly') {
        const dayNames = ['', '週一', '週二', '週三', '週四', '週五', '週六', '週日'];
        const days = (r.weeklyDays || []).map(d => dayNames[d]).join('、');
        return `每週 ${days || '—'} ${String(r.sendHour).padStart(2, '0')}:00`;
      }
      if (r.recurrenceType === 'monthly') {
        const days = (r.monthlyDays || []).map(d => d === 'last' ? '最後一天' : `${d}日`).join('、');
        return `每月 ${days || '—'} ${String(r.sendHour).padStart(2, '0')}:00`;
      }
      if (r.recurrenceType === 'yearly') {
        if (r.yearlyType === 'lunar_festival') {
          const f = this.festivals.find(f => f.code === r.festival);
          const fname = f ? f.name : r.festival;
          const offset = r.offsetDays < 0 ? `前 ${Math.abs(r.offsetDays)} 天` : '當天';
          return `每年 ${fname}${offset} ${String(r.sendHour).padStart(2, '0')}:00`;
        }
        const dates = (r.yearlyDates || []).map(d => `${d.month}/${d.day}`).join('、');
        return `每年 ${dates} ${String(r.sendHour).padStart(2, '0')}:00`;
      }
      return '';
    },

    stepContentFilled() {
      return [
        // Step 0: 活動名稱 AND LINE 帳號都必須有值
        !!(this.form.campaignName && this.form.lineConfigId),
        // Step 1: 至少一則訊息有內容
        this.form.messages.some(m => {
          if (m._inputMode === 'text') return !!(m._text || '').trim();
          if (m._inputMode === 'image') return !!m._imageUrl;
          return !!m._template;
        }),
        // Step 2: 受眾有選擇
        !!(this.form.audienceType === 'all' || this.form.ruleId || this.selectedMembers.length > 0),
        // Step 3: 永遠有預設值
        true
      ];
    }
  },
  async mounted() {
    this.loadLineConfigs();
    this.loadAudienceRules();
    this.loadFestivals();
    if (this.isRecurringEdit) {
      await this.loadRecurringConfig();
    } else if (this.isEditMode) {
      await this.loadBroadcast();
    }
  },
  watch: {
    'form.audienceType'() {
      this.handleAudienceChange();
    }
  },
  methods: {
    goStep(idx) {
      if (!this.isStepClickable(idx)) return;
      this.stepErrors = {};
      this.currentStep = idx;
    },

    isStepClickable(idx) {
      // 已到達過的步驟可自由跳轉
      if (idx <= this.maxReachedStep) return true;
      // 只能往前跳一步，且每個前置步驟都已有內容
      return this.stepContentFilled.slice(0, idx).every(Boolean);
    },

    validateStep(step) {
      this.stepErrors = {};
      if (step === 0) {
        if (!this.form.campaignName) this.stepErrors.campaignName = '請填寫活動名稱';
        if (!this.form.lineConfigId) this.stepErrors.lineConfigId = '請選擇 LINE 官方帳號';
      } else if (step === 1) {
        if (this.form.messages.length === 0) {
          this.stepErrors.messages = '請至少新增一則訊息';
        }
        this.form.messages.forEach((msg, idx) => {
          if (msg._inputMode === 'text') {
            if (!(msg._text || '').trim()) this.stepErrors[`msg_${idx}`] = '請輸入文字內容';
            else if ((msg._text || '').length > 5000) this.stepErrors[`msg_${idx}`] = '文字超過 5000 字元限制';
          } else if (msg._inputMode === 'image') {
            if (!msg._imageUrl) this.stepErrors[`msg_${idx}`] = '請上傳圖片';
          } else {
            if (!msg._template) this.stepErrors[`msg_${idx}`] = '請選擇 LINE 訊息範本';
          }
        });
      } else if (step === 2) {
        if (this.form.audienceType === 'rule' && !this.form.ruleId) {
          this.stepErrors.audience = '請選擇受眾規則';
        }
        if (this.form.audienceType === 'members' && this.selectedMembers.length === 0) {
          this.stepErrors.audience = '請至少選擇一位會員';
        }
      } else if (step === 3) {
        if (this.form.sendType === 'scheduled') {
          if (!this.form.scheduledAt) {
            this.stepErrors.scheduledAt = '請選擇排程時間';
          } else if (new Date(this.form.scheduledAt) <= new Date()) {
            this.stepErrors.scheduledAt = '排程時間必須在未來';
          }
        } else if (this.form.sendType === 'recurring') {
          const r = this.form.recurring;
          if (r.recurrenceType === 'weekly' && (!r.weeklyDays || r.weeklyDays.length === 0)) {
            this.stepErrors.recurring = '請選擇至少一個星期幾';
          }
          if (r.recurrenceType === 'monthly' && (!r.monthlyDays || r.monthlyDays.length === 0)) {
            this.stepErrors.recurring = '請選擇至少一個日期';
          }
          if (r.recurrenceType === 'yearly' && r.yearlyType === 'gregorian' && (!r.yearlyDates || r.yearlyDates.length === 0)) {
            this.stepErrors.recurring = '請至少新增一個日期';
          }
        }
      }
      return Object.keys(this.stepErrors).length === 0;
    },

    nextStep() {
      if (!this.validateStep(this.currentStep)) return;
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
        if (this.currentStep > this.maxReachedStep) {
          this.maxReachedStep = this.currentStep;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    formatScheduledAt(val) {
      if (!val) return '';
      return new Date(val).toLocaleString('zh-TW', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      });
    },

    async loadLineConfigs() {
      this.loadingConfigs = true;
      try {
        const response = await lineConfigApi.list({ pageSize: 100, isActive: true });
        if (response && response.success && response.data) {
          this.lineConfigs = response.data.items || response.data || [];
          if (this.lineConfigs.length === 1 && !this.form.lineConfigId) {
            this.form.lineConfigId = this.lineConfigs[0].id;
            this.handleAudienceChange();
          }
        }
      } catch (err) {
        console.error('載入 LINE 設定失敗:', err);
        await showError('載入失敗', '無法載入 LINE 官方帳號設定');
      } finally {
        this.loadingConfigs = false;
      }
    },

    async loadAudienceRules() {
      this.loadingRules = true;
      try {
        const response = await audienceRuleService.getAudienceRules({ pageSize: 100, isActive: true });
        if (response && response.success && response.data) {
          this.audienceRules = response.data.items || response.data || [];
        }
      } catch (err) {
        console.error('載入受眾規則失敗:', err);
      } finally {
        this.loadingRules = false;
      }
    },

    async loadBroadcast() {
      this.loading = true;
      try {
        const response = await lineBroadcastService.getBroadcast(this.$route.params.id);
        if (response && response.success && response.data) {
          const broadcast = response.data;
          if (!broadcast) {
            await showError('載入失敗', '無法載入推播資料');
            this.$router.push({ name: 'ClientLineBroadcastList' });
            return;
          }
          if (broadcast.status !== 'draft') {
            await showError('無法編輯', '只有草稿狀態的推播才能編輯');
            this.$router.push({ name: 'ClientLineBroadcastList' });
            return;
          }
          this.form.campaignName = broadcast.campaignName || '';
          this.form.lineConfigId = broadcast.lineConfigIds && broadcast.lineConfigIds.length > 0
            ? broadcast.lineConfigIds[0] : '';
          // 還原多則訊息（_multi wrapper 格式）
          const rawMsgs = broadcast.messageContent?._multi && Array.isArray(broadcast.messageContent.messages)
            ? broadcast.messageContent.messages
            : [];
          if (rawMsgs.length > 0) {
            this.form.messages = rawMsgs.map((mc, i) => {
              if (mc.type === 'text' && !mc._templateId) {
                return { _id: i + 1, _inputMode: 'text', _template: null, _text: mc.text || '', _imageUrl: '', _collapsed: i > 0 };
              }
              if (mc.type === 'image' && !mc._templateId) {
                return { _id: i + 1, _inputMode: 'image', _template: null, _text: '', _imageUrl: mc.originalContentUrl || '', _collapsed: i > 0 };
              }
              // template 模式：暫時用空 template，等 templateId 載入
              return { _id: i + 1, _inputMode: 'template', _template: null, _text: '', _imageUrl: '', _collapsed: i > 0, _templateId: mc._templateId };
            });
            // 非同步載入各 slot 對應的範本
            const { lineMsgTemplateService } = await import('@/api/modules/lineMsgTemplates.js');
            this.form.messages.forEach(async (msg, i) => {
              if (msg._inputMode === 'template' && msg._templateId) {
                const res = await lineMsgTemplateService.getById(msg._templateId).catch(() => null);
                if (res?.data) this.form.messages[i]._template = res.data;
              }
            });
          } else {
            this.form.messages = [{ _id: 1, _inputMode: 'template', _template: null, _text: '', _collapsed: false }];
          }
          if (broadcast.audienceType) {
            this.form.audienceType = broadcast.audienceType;
            if (broadcast.audienceType === 'rule' && broadcast.audienceConfig) {
              this.form.ruleId = broadcast.audienceConfig.ruleId || '';
            } else if (broadcast.audienceType === 'members' && broadcast.audienceConfig) {
              const memberIds = broadcast.audienceConfig.memberIds || [];
              if (memberIds.length > 0) {
                try {
                  const { memberService } = await import('@/api/modules/members.js');
                  const res = await memberService.getMembersByIdentifiers(memberIds);
                  this.selectedMembers = (res?.data?.members || res?.data || []).map(m => ({
                    id: m.id,
                    name: m.name,
                    phone: m.phone || ''
                  }));
                } catch (e) {
                  console.error('還原受眾會員失敗:', e);
                  this.selectedMembers = [];
                }
              } else {
                this.selectedMembers = [];
              }
            }
          }
          if (broadcast.scheduledAt) {
            this.form.sendType = 'scheduled';
            const d = new Date(broadcast.scheduledAt);
            const pad = n => String(n).padStart(2, '0');
            this.form.scheduledAt = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          } else {
            this.form.sendType = 'immediate';
          }
          this.handleAudienceChange();
          // 草稿載入後，開放所有步驟可點擊
          this.maxReachedStep = this.steps.length - 1;
        } else {
          await showError('載入失敗', response?.message || '無法載入推播資料');
          this.$router.push({ name: 'ClientLineBroadcastList' });
        }
      } catch (err) {
        await showError('載入失敗', err.response?.data?.message || err.message || '無法載入推播資料');
        this.$router.push({ name: 'ClientLineBroadcastList' });
      } finally {
        this.loading = false;
      }
    },

    async loadRecurringConfig() {
      this.loading = true;
      try {
        const response = await lineBroadcastRecurringService.get(this.$route.params.id);
        const config = response?.data;
        if (!response?.success || !config) {
          await showError('載入失敗', response?.message || '無法載入週期推播設定');
          this.$router.push({ name: 'ClientLineBroadcastList' });
          return;
        }
        if (config.status === 'archived') {
          await showError('無法編輯', '已封存的週期推播設定無法編輯');
          this.$router.push({ name: 'ClientLineBroadcastList' });
          return;
        }

        this.form.sendType = 'recurring';
        this.form.campaignName = config.configName || '';
        this.form.lineConfigId = Array.isArray(config.lineConfigIds) && config.lineConfigIds.length > 0
          ? config.lineConfigIds[0] : '';

        // 還原多則訊息（_multi wrapper 格式）
        const rawMsgs = config.messageContent?._multi && Array.isArray(config.messageContent.messages)
          ? config.messageContent.messages
          : [];
        if (rawMsgs.length > 0) {
          this.form.messages = rawMsgs.map((mc, i) => {
            if (mc.type === 'text' && !mc._templateId) {
              return { _id: i + 1, _inputMode: 'text', _template: null, _text: mc.text || '', _imageUrl: '', _collapsed: i > 0 };
            }
            if (mc.type === 'image' && !mc._templateId) {
              return { _id: i + 1, _inputMode: 'image', _template: null, _text: '', _imageUrl: mc.originalContentUrl || '', _collapsed: i > 0 };
            }
            return { _id: i + 1, _inputMode: 'template', _template: null, _text: '', _imageUrl: '', _collapsed: i > 0, _templateId: mc._templateId };
          });
          const { lineMsgTemplateService } = await import('@/api/modules/lineMsgTemplates.js');
          this.form.messages.forEach(async (msg, i) => {
            if (msg._inputMode === 'template' && msg._templateId) {
              const res = await lineMsgTemplateService.getById(msg._templateId).catch(() => null);
              if (res?.data) this.form.messages[i]._template = res.data;
            }
          });
        }

        // 受眾還原
        if (config.audienceType) {
          this.form.audienceType = config.audienceType;
          if (config.audienceType === 'rule' && config.audienceConfig) {
            this.form.ruleId = config.audienceConfig.ruleId || '';
          } else if (config.audienceType === 'members' && config.audienceConfig) {
            const memberIds = config.audienceConfig.memberIds || [];
            if (memberIds.length > 0) {
              try {
                const { memberService } = await import('@/api/modules/members.js');
                const res = await memberService.getMembersByIdentifiers(memberIds);
                this.selectedMembers = (res?.data?.members || res?.data || []).map(m => ({
                  id: m.id, name: m.name, phone: m.phone || ''
                }));
              } catch (e) {
                console.error('還原受眾會員失敗:', e);
                this.selectedMembers = [];
              }
            }
          }
        }

        // 週期設定還原
        const r = this.form.recurring;
        r.recurrenceType = config.recurrenceType || 'daily';
        r.sendHour = config.sendHour ?? 9;
        r.sendMinute = config.sendMinute ?? 0;
        const rc = config.recurrenceConfig || {};
        if (r.recurrenceType === 'weekly') {
          r.weeklyDays = Array.isArray(rc.days) ? [...rc.days] : [];
        } else if (r.recurrenceType === 'monthly') {
          r.monthlyDays = Array.isArray(rc.days) ? [...rc.days] : [];
        } else if (r.recurrenceType === 'yearly') {
          if (rc.type === 'lunar_festival') {
            r.yearlyType = 'lunar_festival';
            r.festival = rc.festival || 'spring_new_year';
            r.offsetDays = rc.offset_days ?? 0;
          } else {
            r.yearlyType = 'gregorian';
            r.yearlyDates = Array.isArray(rc.dates) && rc.dates.length > 0
              ? rc.dates.map(d => ({ month: d.month, day: d.day }))
              : [{ month: 1, day: 1 }];
          }
        }
        r.startDate = config.startDate ? String(config.startDate).slice(0, 10) : '';
        r.endDate = config.endDate ? String(config.endDate).slice(0, 10) : '';
        r.maxExecutions = config.maxExecutions || '';
        if (r.startDate || r.endDate || r.maxExecutions) {
          this.showRecurringAdvanced = true;
        }

        this.handleAudienceChange();
        this.maxReachedStep = this.steps.length - 1;
      } catch (err) {
        await showError('載入失敗', err.response?.data?.message || err.message || '無法載入週期推播設定');
        this.$router.push({ name: 'ClientLineBroadcastList' });
      } finally {
        this.loading = false;
      }
    },

    handleAudienceTypeChange() {
      if (this.form.audienceType === 'rule') {
        this.selectedMembers = [];
      } else if (this.form.audienceType === 'members') {
        this.form.ruleId = '';
      } else if (this.form.audienceType === 'all') {
        this.selectedMembers = [];
        this.form.ruleId = '';
      }
      this.handleAudienceChange();
    },

    removeMember(memberId) {
      const index = this.selectedMembers.findIndex(m => m.id === memberId);
      if (index > -1) {
        this.selectedMembers.splice(index, 1);
        this.handleAudienceChange();
      }
    },

    clearAllMembers() {
      this.selectedMembers = [];
      this.memberTab = 'search';
      this.$refs.searchRef?.reset();
      this.$refs.tagSearchRef?.reset();
      this.$refs.csvRef?.reset();
      this.handleAudienceChange();
    },

    handleAudienceChange() {
      if (this.previewTimeout) clearTimeout(this.previewTimeout);
      this.previewTimeout = setTimeout(() => {
        if (this.canPreview) {
          this.previewAudience();
        } else {
          this.previewCount = null;
          this.previewError = null;
        }
      }, 500);
    },

    async previewAudience() {
      if (!this.canPreview) {
        this.previewCount = null;
        this.previewError = null;
        return;
      }

      this.previewLoading = true;
      this.previewError = null;
      try {
        let audienceConfig = {};
        if (this.form.audienceType === 'rule') {
          if (!this.form.ruleId) { this.previewError = '請選擇受眾規則'; return; }
          audienceConfig = { ruleId: this.form.ruleId };
        } else if (this.form.audienceType === 'members') {
          if (this.selectedMembers.length === 0) { this.previewError = '請選擇至少一個會員'; return; }
          audienceConfig = { memberIds: this.selectedMembers.map(m => m.id) };
        }
        // audienceType === 'all' 不需要 audienceConfig，直接送出
        const response = await lineBroadcastService.previewAudience({
          audienceType: this.form.audienceType,
          audienceConfig: Object.keys(audienceConfig).length > 0 ? audienceConfig : null,
          lineConfigIds: [this.form.lineConfigId]
        });
        if (response && response.success && response.data) {
          this.previewCount = response.data.estimatedCount || 0;
        } else {
          this.previewError = response?.message || '預覽失敗';
        }
      } catch (err) {
        this.previewError = err.response?.data?.message || err.message || '預覽失敗';
        this.previewCount = null;
      } finally {
        this.previewLoading = false;
      }
    },

    _newMsg(id) {
      return { _id: id, _inputMode: 'text', _template: null, _text: '', _imageUrl: '', _collapsed: false };
    },

    _toPreviewData(t) {
      if (!t) return null;
      const mc = t.messageContent || {};
      const tf = t.templateFields || {};
      const type = t.messageType;
      const IMAGEMAP_LAYOUTS = {
        full: [{ x: 0, y: 0, w: 1, h: 1 }],
        h2: [{ x: 0, y: 0, w: 1, h: .5 }, { x: 0, y: .5, w: 1, h: .5 }],
        v2: [{ x: 0, y: 0, w: .5, h: 1 }, { x: .5, y: 0, w: .5, h: 1 }],
        h3: [{ x: 0, y: 0, w: 1, h: 1/3 }, { x: 0, y: 1/3, w: 1, h: 1/3 }, { x: 0, y: 2/3, w: 1, h: 1/3 }],
        quad: [{ x: 0, y: 0, w: .5, h: .5 }, { x: .5, y: 0, w: .5, h: .5 }, { x: 0, y: .5, w: .5, h: .5 }, { x: .5, y: .5, w: .5, h: .5 }],
        t1b2: [{ x: 0, y: 0, w: 1, h: .5 }, { x: 0, y: .5, w: .5, h: .5 }, { x: .5, y: .5, w: .5, h: .5 }],
        t2b1: [{ x: 0, y: 0, w: .5, h: .5 }, { x: .5, y: 0, w: .5, h: .5 }, { x: 0, y: .5, w: 1, h: .5 }],
      };
      let textContent = '', imageUrl = '', previewImageUrl = '', imagemapUrl = '';
      let textEmojis = [];
      let imagemapAreas = [], carouselItems = [], flexContent = null;
      if (type === 'text') {
        textContent = mc.text || '';
        textEmojis = Array.isArray(mc.emojis) ? mc.emojis : [];
      } else if (type === 'image') {
        imageUrl = mc.originalContentUrl || '';
      } else if (type === 'video') {
        previewImageUrl = mc.previewImageUrl || '';
      } else if (type === 'image_carousel') {
        carouselItems = (mc.template?.columns || []).map(c => ({
          imageUrl: c.imageUrl || '',
          label: c.action?.label || ''
        }));
      } else if (type === 'imagemap') {
        imagemapUrl = mc.baseUrl || '';
        imagemapAreas = tf.layout ? (IMAGEMAP_LAYOUTS[tf.layout] || []) : [];
      } else if (type === 'flex' || type === 'flex_carousel') {
        if (mc.type === 'flex' && mc.contents) flexContent = mc.contents;
        else flexContent = mc;
      }
      return { messageType: type, textContent, textEmojis, imageUrl, previewImageUrl, imagemapUrl, imagemapAreas, carouselItems, flexContent };
    },


    addMsg() {
      if (this.form.messages.length >= 5) return;
      // 收合現有訊息
      this.form.messages.forEach(m => { m._collapsed = true; });
      const maxId = this.form.messages.reduce((m, r) => Math.max(m, r._id), 0);
      this.form.messages.push(this._newMsg(maxId + 1));
    },

    removeMsg(idx) {
      if (this.form.messages.length <= 1) return;
      this.form.messages.splice(idx, 1);
    },

    moveMsg(idx, dir) {
      const arr = this.form.messages;
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return;
      const tmp = arr[idx];
      arr.splice(idx, 1);
      arr.splice(target, 0, tmp);
    },

    _slotToMessageContent(msg) {
      if (msg._inputMode === 'text') {
        return { type: 'text', text: msg._text || '' };
      }
      if (msg._inputMode === 'image') {
        if (!msg._imageUrl) return null;
        return { type: 'image', originalContentUrl: msg._imageUrl, previewImageUrl: msg._imageUrl };
      }
      if (msg._template) {
        return this._templateToLineMessage(msg._template);
      }
      return null;
    },

    /**
     * 將 LINE 訊息範本轉成推播可存／可送的 LINE API 訊息物件
     * 七種類型皆對齊 Messaging API；_templateId 僅供編輯還原，後端發送前會移除
     */
    _templateToLineMessage(t) {
      const mc = t.messageContent || {};
      const tid = t.id;
      switch (t.messageType) {
        case 'text': {
          const out = { type: 'text', text: mc.text || '', _templateId: tid };
          if (Array.isArray(mc.emojis) && mc.emojis.length > 0) out.emojis = mc.emojis;
          return out;
        }
        case 'image':
          return {
            type: 'image',
            originalContentUrl: mc.originalContentUrl,
            previewImageUrl: mc.previewImageUrl || mc.originalContentUrl,
            _templateId: tid
          };
        case 'video':
          return {
            type: 'video',
            originalContentUrl: mc.originalContentUrl,
            previewImageUrl: mc.previewImageUrl,
            _templateId: tid
          };
        case 'flex':
        case 'flex_carousel': {
          // 範本存的是 bubble/carousel，必須包成 flex（不可直接送 bubble）
          const contents = (mc.type === 'flex' && mc.contents) ? mc.contents : mc;
          return {
            type: 'flex',
            altText: t.altText || '訊息',
            contents,
            _templateId: tid
          };
        }
        case 'image_carousel':
          // 已是 { type:'template', altText, template:{ type:'image_carousel', ... } }
          return { ...mc, type: mc.type || 'template', _templateId: tid };
        case 'imagemap':
          return {
            type: 'imagemap',
            baseUrl: mc.baseUrl,
            altText: mc.altText || t.altText || '圖文訊息',
            baseSize: mc.baseSize,
            actions: mc.actions || [],
            _templateId: tid
          };
        default:
          return { ...mc, _templateId: tid };
      }
    },

    async uploadImage(file) {
      const { lineMsgTemplateService } = await import('@/api/modules/lineMsgTemplates.js');
      return lineMsgTemplateService.uploadImage(file);
    },

    _buildBroadcastData(isDraft = false) {
      const messageContents = this.form.messages
        .map(m => this._slotToMessageContent(m))
        .filter(Boolean);
      let audienceConfig = {};
      if (this.form.audienceType === 'rule' && this.form.ruleId) {
        audienceConfig = { ruleId: this.form.ruleId };
      } else if (this.form.audienceType === 'members' && this.selectedMembers.length > 0) {
        audienceConfig = { memberIds: this.selectedMembers.map(m => m.id) };
      }
      const data = {
        campaignName: this.form.campaignName,
        messageContents,
        lineConfigIds: this.form.lineConfigId ? [this.form.lineConfigId] : [],
        audienceType: this.form.audienceType || null,
        audienceConfig: Object.keys(audienceConfig).length > 0 ? audienceConfig : null
      };
      if (isDraft) data.saveAsDraft = true;
      if (this.form.sendType === 'scheduled' && this.form.scheduledAt) {
        data.scheduledAt = new Date(this.form.scheduledAt).toISOString();
      }
      return data;
    },

    async handleSubmit() {
      // 逐步驗證所有步驟，遇到第一個失敗時跳回該步驟
      for (let step = 0; step < this.steps.length; step++) {
        if (!this.validateStep(step)) {
          this.currentStep = step;
          window.scrollTo({ top: 0, behavior: 'smooth' });
          const stepLabel = this.steps[step]?.label || `步驟 ${step + 1}`;
          await showError('請填寫完整必填欄位', `「${stepLabel}」尚有欄位未填寫`);
          return;
        }
      }
      if (!this.isFormValid) {
        await showError('表單驗證失敗', '請填寫所有必填欄位');
        return;
      }

      if (this.form.sendType === 'recurring') {
        return this.handleSubmitRecurring();
      }

      const isScheduled = this.form.sendType === 'scheduled';
      const audienceDesc = this.previewCount != null ? `${this.previewCount.toLocaleString()} 位` : '符合條件的';
      const confirmTitle = isScheduled ? '確認設定排程發送？' : '確認發送推播？';
      const confirmText = isScheduled
        ? `推播訊息將於 ${this.formatScheduledAt(this.form.scheduledAt)} 發送給 ${audienceDesc} LINE 好友`
        : `推播訊息將在約 1 分鐘內發送給 ${audienceDesc} LINE 好友，送出後無法撤回`;
      const confirmBtnText = isScheduled ? '確認排程' : '確認發送';
      const confirmed = await showConfirm(confirmTitle, confirmText, confirmBtnText);
      if (!confirmed) return;

      this.loading = true;
      try {
        // CSV 上傳備份
        if (this.form.audienceType === 'members') {
          const csvFile = this.$refs.csvRef?.csvFile;
          if (csvFile) {
            try {
              this.$refs.csvRef.setCsvUploadStatus('uploading');
              const { couponService } = await import('@/api/modules/coupons.js');
              const formData = new FormData();
              formData.append('file', csvFile);
              const uploadRes = await couponService.uploadFile(formData);
              if (uploadRes?.success && uploadRes.data?.fileUrl) {
                this.$refs.csvRef.setCsvUploadStatus('done');
              } else {
                this.$refs.csvRef.setCsvUploadStatus('error');
              }
            } catch (e) {
              this.$refs.csvRef.setCsvUploadStatus('error');
            }
          }
        }

        const data = this._buildBroadcastData(false);
        let response;
        if (this.isEditMode) {
          response = await lineBroadcastService.updateBroadcast(this.$route.params.id, data);
          if (response?.success) {
            await showSuccess('更新成功', '推播活動已更新');
            this.$router.push({ name: 'ClientLineBroadcastList' });
          } else {
            await showError('更新失敗', response?.message || '更新推播活動失敗');
          }
        } else {
          response = await lineBroadcastService.createBroadcast(data);
          if (response?.success) {
            await showSuccess('建立成功', '推播活動已建立');
            this.$router.push({ name: 'ClientLineBroadcastList' });
          } else {
            await showError('建立失敗', response?.message || '建立推播活動失敗');
          }
        }
      } catch (err) {
        await showError('建立失敗', err.response?.data?.message || err.message || '建立推播活動失敗');
      } finally {
        this.loading = false;
      }
    },

    async handleSaveDraft() {
      if (!this.canSaveDraft) {
        await showError('無法儲存', '請至少填寫活動名稱');
        return;
      }
      this.savingDraft = true;
      try {
        const data = this._buildBroadcastData(true);
        let response;
        if (this.isEditMode) {
          response = await lineBroadcastService.updateBroadcast(this.$route.params.id, data);
          if (response?.success) {
            await showSuccess('更新成功', '草稿已更新');
            this.$router.push({ name: 'ClientLineBroadcastList' });
          } else {
            await showError('更新失敗', response?.message || '更新草稿失敗');
          }
        } else {
          response = await lineBroadcastService.createBroadcast(data);
          if (response?.success) {
            await showSuccess('儲存成功', '推播活動已儲存為草稿');
            this.$router.push({ name: 'ClientLineBroadcastList' });
          } else {
            await showError('儲存失敗', response?.message || '儲存草稿失敗');
          }
        }
      } catch (err) {
        await showError('儲存失敗', err.response?.data?.message || err.message || '儲存草稿失敗');
      } finally {
        this.savingDraft = false;
      }
    },

    handleCancel() {
      this.$router.push({ name: 'ClientLineBroadcastList' });
    },

    async loadFestivals() {
      this.loadingFestivals = true;
      try {
        const res = await lineBroadcastRecurringService.getFestivals();
        if (res?.success && res.data) {
          this.festivals = res.data;
        }
      } catch (e) {
        console.error('載入農曆節慶失敗:', e);
      } finally {
        this.loadingFestivals = false;
      }
    },

    addYearlyDate() {
      if (this.form.recurring.yearlyDates.length >= 12) return;
      const last = this.form.recurring.yearlyDates.slice(-1)[0] || { month: 1, day: 1 };
      this.form.recurring.yearlyDates.push({ month: last.month, day: 1 });
    },

    removeYearlyDate(idx) {
      if (this.form.recurring.yearlyDates.length <= 1) return;
      this.form.recurring.yearlyDates.splice(idx, 1);
    },

    toggleMonthlyDay(day) {
      const days = this.form.recurring.monthlyDays;
      const idx = days.indexOf(day);
      if (idx === -1) {
        days.push(day);
        days.sort((a, b) => {
          if (a === 'last') return 1;
          if (b === 'last') return -1;
          return a - b;
        });
      } else {
        days.splice(idx, 1);
      }
    },

    _buildRecurringData() {
      const r = this.form.recurring;
      const messageContents = this.form.messages
        .map(m => this._slotToMessageContent(m))
        .filter(Boolean);
      let audienceConfig = {};
      if (this.form.audienceType === 'rule' && this.form.ruleId) {
        audienceConfig = { ruleId: this.form.ruleId };
      } else if (this.form.audienceType === 'members' && this.selectedMembers.length > 0) {
        audienceConfig = { memberIds: this.selectedMembers.map(m => m.id) };
      }

      let recurrenceConfig = {};
      if (r.recurrenceType === 'weekly') {
        recurrenceConfig = { days: r.weeklyDays };
      } else if (r.recurrenceType === 'monthly') {
        recurrenceConfig = { days: r.monthlyDays };
      } else if (r.recurrenceType === 'yearly') {
        if (r.yearlyType === 'gregorian') {
          recurrenceConfig = { type: 'gregorian', dates: r.yearlyDates };
        } else {
          recurrenceConfig = { type: 'lunar_festival', festival: r.festival, offset_days: r.offsetDays };
        }
      }

      return {
        configName: this.form.campaignName,
        messageContents,
        lineConfigIds: this.form.lineConfigId ? [this.form.lineConfigId] : [],
        audienceType: this.form.audienceType || null,
        audienceConfig: Object.keys(audienceConfig).length > 0 ? audienceConfig : null,
        recurrenceType: r.recurrenceType,
        recurrenceConfig,
        sendHour: r.sendHour,
        sendMinute: r.sendMinute || 0,
        startDate: r.startDate || null,
        endDate: r.endDate || null,
        maxExecutions: r.maxExecutions ? parseInt(r.maxExecutions, 10) : null
      };
    },

    async handleSubmitRecurring() {
      this.loading = true;
      try {
        const data = this._buildRecurringData();
        const isEdit = this.isRecurringEdit;
        const confirmed = await showConfirm(
          isEdit ? '確認儲存週期推播設定？' : '確認建立週期推播？',
          `週期設定：${this.recurringDescription}`,
          isEdit ? '確認儲存' : '確認建立'
        );
        if (!confirmed) return;

        const res = isEdit
          ? await lineBroadcastRecurringService.update(this.$route.params.id, data)
          : await lineBroadcastRecurringService.create(data);
        if (res?.success) {
          await showSuccess(isEdit ? '儲存成功' : '建立成功', isEdit ? '週期推播設定已更新' : '週期推播設定已建立');
          this.$router.push({ name: 'ClientLineBroadcastList' });
        } else {
          await showError(isEdit ? '儲存失敗' : '建立失敗', res?.message || '操作失敗');
        }
      } catch (err) {
        await showError(this.isRecurringEdit ? '儲存失敗' : '建立失敗', err.response?.data?.message || err.message || '操作失敗');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* ---- 頁面外層與固定 Header ---- */
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-right: 0;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding-top: 72px;
}

.btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
}
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-primary {
  background: #3b82f6;
  color: #fff;
  border: none;
}
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-outline-primary {
  background: #fff;
  color: #3b82f6;
  border: 1px solid #3b82f6;
}
.btn-outline-primary:hover:not(:disabled) { background: #eff6ff; }
.btn-outline-primary:disabled { opacity: 0.6; cursor: not-allowed; }

/* ---- 步驟指示器 ---- */
.steps-bar {
  display: flex;
  align-items: flex-start;
  background: #fff;
  border-radius: 12px;
  padding: 24px 32px;
  margin-bottom: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08);
  position: relative;
}

.step-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  cursor: default;
}

.step-item.clickable {
  cursor: pointer;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e5e7eb;
  color: #9ca3af;
  font-weight: 600;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .25s;
  z-index: 1;
}

.step-item.active .step-circle {
  background: #3b82f6;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(59,130,246,.2);
}

.step-item.done .step-circle {
  background: #10b981;
  color: #fff;
}

.step-label {
  margin-top: 8px;
  font-size: 12px;
  color: #9ca3af;
  text-align: center;
  white-space: nowrap;
}

.step-item.active .step-label {
  color: #3b82f6;
  font-weight: 600;
}

.step-item.done .step-label {
  color: #10b981;
}

.step-line {
  position: absolute;
  top: 18px;
  left: calc(50% + 18px);
  width: calc(100% - 36px);
  height: 2px;
  background: #e5e7eb;
  z-index: 0;
}

.step-line.done {
  background: #10b981;
}

/* ---- 步驟內容 ---- */
.step-content {
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08);
  min-height: 360px;
}

.section-header-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
}

.section-header-row h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
}

.section-icon {
  font-size: 22px;
  color: #3b82f6;
}

/* ---- 表單基礎 ---- */
.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.required-label::after {
  content: ' *';
  color: #ef4444;
}

.form-control {
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color .2s;
  width: 100%;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,.1);
}

.form-control.is-invalid {
  border-color: #ef4444;
}

.error-text {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #6b7280;
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.form-text {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

/* ---- LINE 帳號卡片 ---- */
.line-config-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.config-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all .2s;
  min-width: 200px;
}

.config-card input {
  display: none;
}

.config-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.config-card.disabled {
  opacity: .5;
  cursor: not-allowed;
}

.config-card-body {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-icon {
  font-size: 24px;
  color: #06c755;
}

.config-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.config-followers {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #06c755;
  margin-top: 3px;
}

.config-followers i {
  font-size: 13px;
}

.followers-unknown {
  color: #aaa;
}

/* ---- 訊息數量徽章 ---- */
.msg-count-badge {
  margin-left: auto;
  background: #eff6ff;
  color: #3b82f6;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 2px 12px;
  font-size: 13px;
  font-weight: 600;
}

/* ---- 多訊息列表 ---- */
.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.message-block {
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: border-color .2s;
}

.message-block.has-error {
  border-color: #ef4444;
}

.msg-block-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.msg-index {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
}

/* 訊息類型 tab 按鈕 */
.msg-type-tabs {
  display: flex;
  gap: 4px;
  flex: 1;
  flex-wrap: wrap;
}

.msg-type-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 20px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
}

.msg-type-tab.active {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #3b82f6;
}

.msg-type-tab:disabled {
  opacity: .45;
  cursor: not-allowed;
}

/* ---- 模式切換 tab ---- */
.input-mode-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 14px;
  border-bottom: 2px solid #e5e7eb;
}

.mode-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: -2px;
  transition: all .15s;
}

.mode-tab:hover { color: #3b82f6; }
.mode-tab.active { color: #3b82f6; border-bottom-color: #3b82f6; font-weight: 600; }

/* 文字輸入區 */
.text-input-wrap {
  display: flex;
  flex-direction: column;
}

/* 圖片上傳區 */
.image-upload-wrap {
  display: flex;
  flex-direction: column;
}

.message-textarea {
  resize: vertical;
  min-height: 140px;
  font-size: 14px;
  line-height: 1.6;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
}
.message-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,.1);
}
.message-textarea.is-invalid { border-color: #ef4444; }

.char-count {
  font-size: 12px;
  color: #9ca3af;
  text-align: right;
  margin-top: 4px;
}
.char-count.danger { color: #ef4444; }

/* 折疊按鈕 */
.collapse-btn {
  flex-shrink: 0;
  border-color: transparent;
  background: transparent;
  color: #9ca3af;
}
.collapse-btn:hover:not(:disabled) {
  border-color: #d1d5db;
  color: #374151;
}

/* 收合摘要文字 */
.msg-collapsed-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.msg-collapsed-summary i {
  color: #3b82f6;
  flex-shrink: 0;
}

/* 操作按鈕群 */
.msg-block-actions {
  display: flex;
  gap: 4px;
  margin-left: auto;
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  font-size: 16px;
  transition: all .15s;
}

.icon-btn:hover:not(:disabled) {
  border-color: #9ca3af;
  color: #374151;
}

.icon-btn.danger:hover:not(:disabled) {
  border-color: #ef4444;
  color: #ef4444;
}

.icon-btn:disabled {
  opacity: .35;
  cursor: not-allowed;
}

/* 訊息 body */
.msg-block-body {
  padding: 16px;
}

/* 圖片預覽 */
.img-preview-box {
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  max-width: 240px;
}

.img-preview-box img {
  width: 100%;
  display: block;
}

/* 貼圖雙欄 */
.sticker-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Flex 連結 */
.help-link {
  font-size: 12px;
  margin-left: 8px;
  color: #3b82f6;
}

/* code textarea */
.code-textarea {
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  resize: vertical;
  min-height: 160px;
}

/* 新增訊息按鈕 */
.btn-add-msg {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 13px;
  border: 2px dashed #93c5fd;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  justify-content: center;
  transition: all .2s;
  margin-top: 4px;
}

.btn-add-msg i {
  font-size: 18px;
}

.btn-add-msg:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #1d4ed8;
  background: #dbeafe;
}

.btn-add-msg:disabled {
  opacity: .45;
  cursor: not-allowed;
}

/* section desc */
.section-desc {
  color: #6b7280;
  font-size: 13px;
  margin: -8px 0 16px;
}

/* ---- 訊息類型卡片（保留給受眾/發送用）---- */
.message-type-cards,
.audience-type-cards,
.send-type-cards {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 24px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all .2s;
  min-width: 130px;
  text-align: center;
}

.type-card input {
  display: none;
}

.type-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.type-card.selected .type-icon {
  color: #3b82f6;
}

.type-card.disabled {
  opacity: .45;
  cursor: not-allowed;
}

.type-icon {
  font-size: 26px;
  color: #9ca3af;
}

.type-card span {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.type-card small {
  font-size: 11px;
  color: #9ca3af;
}

/* 受眾/發送方式卡片橫排 */
.audience-type-cards .type-card,
.send-type-cards .type-card {
  flex-direction: row;
  align-items: center;
  gap: 14px;
  text-align: left;
  min-width: 200px;
  padding: 16px 20px;
}

.audience-type-cards .type-card div,
.send-type-cards .type-card div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.all-broadcast-notice {
  margin-top: 12px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.5;
}

.all-broadcast-notice i {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* ---- 訊息字數 ---- */
.message-textarea {
  resize: vertical;
  min-height: 180px;
  font-size: 14px;
}

.char-count {
  font-size: 12px;
  color: #9ca3af;
  text-align: right;
  margin-top: 4px;
}

.char-count.danger {
  color: #ef4444;
}

/* ---- Hint box ---- */
.hint-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
}

.hint-warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fcd34d;
}

/* ---- 受眾 Tabs ---- */
.selection-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
}

.tab-button {
  padding: 10px 20px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  transition: all .2s;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: -2px;
}

.tab-button:hover { color: #3b82f6; }
.tab-button.active { color: #3b82f6; border-bottom-color: #3b82f6; font-weight: 500; }
.tab-button:disabled { opacity: .5; cursor: not-allowed; }

/* ---- 已選會員 ---- */
.selected-members-section {
  margin-top: 20px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h4 {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.selected-members-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.selected-member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.member-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.member-name { font-weight: 500; color: #374151; }
.member-phone { font-size: 13px; color: #6b7280; }

.badge-line-friend {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #0958d9;
  background: #e6f4ff;
  border: 1px solid #91caff;
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 6px;
  vertical-align: middle;
}

/* ---- 預覽人數 ---- */
.preview-section { margin-top: 24px; }

.preview-card {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-weight: 600;
  color: #374151;
}

.preview-header i { font-size: 20px; color: #3b82f6; }
.preview-header .btn { margin-left: auto; }

.preview-content {
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #6b7280;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }
.spinning { animation: spin .8s linear infinite; }

.preview-error { display: flex; align-items: center; gap: 8px; color: #ef4444; }
.preview-result { text-align: center; width: 100%; }
.preview-empty { display: flex; align-items: center; gap: 8px; color: #9ca3af; font-size: 14px; }

.preview-count {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.count-value { font-size: 42px; font-weight: 700; color: #3b82f6; line-height: 1; }
.count-label { font-size: 18px; color: #6b7280; font-weight: 500; }
.preview-hint { margin: 0; font-size: 14px; color: #6b7280; display: flex; align-items: center; justify-content: center; gap: 4px; }

/* ---- 摘要卡片 ---- */
.summary-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px 24px;
}

.summary-title {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
}

.summary-key {
  min-width: 90px;
  color: #9ca3af;
  font-weight: 500;
}

.summary-val { color: #374151; }
.summary-val.highlight { color: #3b82f6; font-weight: 700; font-size: 16px; }


.step-actions-right {
  display: flex;
  gap: 12px;
}

/* ---- 通用 Badge ---- */
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.badge-warning { background: #fef3c7; color: #92400e; }

.required { color: #ef4444; }

/* ---- 全域浮動預覽（四個步驟共用） ---- */
.global-preview-sticky {
  position: fixed;
  top: 120px;
  right: 28px;
  width: 320px;
  max-height: calc(100vh - 140px);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 90;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 12px 0 0;
  display: flex;
  flex-direction: column;
}

/* 所有步驟區塊統一向左縮，不被預覽遮住 */
.steps-bar,
.mobile-preview-toggle-bar,
.mobile-preview-drawer,
.step-content {
  margin-right: 348px;
}

.preview-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0;
  padding: 4px 16px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.preview-title-row i {
  font-size: 18px;
  color: #06C755;
}

.preview-count-badge {
  margin-left: auto;
  background: #eff6ff;
  color: #3b82f6;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
}

/* 手機版預覽開關（桌機隱藏） */
.mobile-preview-toggle-bar { display: none; }
.mobile-preview-drawer { display: none; }

@media (max-width: 900px) {
  .global-preview-sticky { display: none; }

  .steps-bar,
  .mobile-preview-toggle-bar,
  .mobile-preview-drawer,
  .step-content,
  .mobile-preview-toggle-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }
  .mobile-preview-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid #06C755;
    border-radius: 20px;
    background: #fff;
    color: #06C755;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all .15s;
  }
  .mobile-preview-toggle-btn:hover { background: #f0fff5; }

  .mobile-preview-drawer {
    display: block;
    margin-bottom: 16px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    background: #fff;
  }
  .mobile-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }
  .mobile-preview-header i { color: #06C755; margin-right: 4px; }
  .mobile-preview-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
  }
  .mobile-preview-close:hover { background: #e5e7eb; }
  .mobile-preview-body { padding: 16px; }
}

/* ---- 週期發送設定 ---- */
.recurrence-type-tabs,
.yearly-type-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.recurrence-tab {
  padding: 6px 18px;
  border: 1.5px solid #d1d5db;
  border-radius: 20px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s;
}
.recurrence-tab.active {
  border-color: #06b6d4;
  background: #ecfeff;
  color: #0891b2;
  font-weight: 600;
}
.recurrence-tab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.weekday-selector {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.weekday-btn {
  width: 52px;
  height: 36px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}
.weekday-btn.active {
  border-color: #06b6d4;
  background: #06b6d4;
  color: #fff;
  font-weight: 600;
}
.weekday-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.monthly-day-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.day-btn {
  width: 40px;
  height: 36px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}
.last-day-btn {
  width: auto;
  padding: 0 12px;
}
.day-btn.active {
  border-color: #06b6d4;
  background: #06b6d4;
  color: #fff;
  font-weight: 600;
}
.day-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.yearly-date-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.festival-offset-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.recurring-advanced-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
  margin-top: 16px;
  user-select: none;
}
.recurring-advanced-toggle:hover { color: #374151; }

.recurring-advanced {
  margin-top: 12px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.form-row-group {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}
</style>

<style>
.page-header-broadcast-create {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-broadcast-create {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-broadcast-create {
    left: 0 !important;
    top: 56px;
  }
}
</style>
